import React, { useState, useCallback, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  Phone, Mail, MapPin, Clock, Send, CheckCircle, ChevronRight,
  AlertCircle, Copy, ClipboardCheck, User, MessageSquare, FileText, Navigation
} from 'lucide-react';
import { practiceInfo } from '../data/siteData';
import { contactAPI } from '../services/api';
import './ContactPage.css';

// ============================================
// Phone formatting helper
// ============================================
function formatPhoneInput(value) {
  const digits = value.replace(/\D/g, '').slice(0, 10);
  if (digits.length <= 3) return digits;
  if (digits.length <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
}

const initialFormState = {
  name: '',
  email: '',
  phone: '',
  subject: 'General Inquiry',
  message: ''
};

function ContactPage() {
  const [formData, setFormData] = useState(initialFormState);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitResult, setSubmitResult] = useState(null);
  const [fieldErrors, setFieldErrors] = useState({});
  const [agreeToSms, setAgreeToSms] = useState(false);
  const [copied, setCopied] = useState(false);
  const [charCount, setCharCount] = useState(0);
  const formRef = useRef(null);

  // Scroll to form when success message is shown
  useEffect(() => {
    if (submitResult && submitResult.type === 'success' && formRef.current) {
      // Wait a bit for the success message to render
      setTimeout(() => {
        formRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 100);
    }
  }, [submitResult]);


  const handleChange = useCallback((e) => {
    const { name, value } = e.target;
    if (name === 'phone') {
      setFormData(prev => ({ ...prev, phone: formatPhoneInput(value) }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
    if (name === 'message') {
      setCharCount(value.length);
    }
    if (fieldErrors[name]) {
      setFieldErrors(prev => ({ ...prev, [name]: '' }));
    }
  }, [fieldErrors]);

  // ============================================
  // Client-side validation
  // ============================================
  const validateForm = () => {
    const errors = {};
    if (!formData.name.trim() || formData.name.trim().length < 2) {
      errors.name = 'Please enter your full name';
    }
    if (!formData.email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      errors.email = 'Please enter a valid email address';
    }
    if (formData.phone) {
      const digits = formData.phone.replace(/\D/g, '');
      if (digits.length > 0 && digits.length !== 10) {
        errors.phone = 'Please enter a valid 10-digit phone number';
      }
    }
    if (!formData.message.trim() || formData.message.trim().length < 10) {
      errors.message = 'Please enter a message (at least 10 characters)';
    }
    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // ============================================
  // Submit
  // ============================================
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setIsSubmitting(true);
    setSubmitResult(null);

    try {
      const response = await contactAPI.send({
        name: formData.name.trim(),
        email: formData.email.trim(),
        phone: formData.phone || null,
        subject: formData.subject,
        message: formData.message.trim(),
        agreeToSms
      });

      setSubmitResult({
        type: 'success',
        message: response.message,
        data: response.data
      });
    } catch (err) {
      const errorMsg = err.errors
        ? (Array.isArray(err.errors) && typeof err.errors[0] === 'object'
            ? err.errors.map(e => e.message).join('. ')
            : err.errors.join('. '))
        : 'Something went wrong. Please try again or call us at (215) 676-0222.';

      setSubmitResult({ type: 'error', message: errorMsg });
    } finally {
      setIsSubmitting(false);
    }
  };

  const resetForm = () => {
    setFormData(initialFormState);
    setSubmitResult(null);
    setFieldErrors({});
    setAgreeToSms(false);
    setCharCount(0);
    setCopied(false);
  };

  const copyReference = (code) => {
    navigator.clipboard.writeText(code).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  const renderFieldError = (field) => {
    if (!fieldErrors[field]) return null;
    return <span className="field-error">{fieldErrors[field]}</span>;
  };

  return (
    <main className="contact-page">
      {/* Hero */}
      <div className="page-hero">
        <div className="page-hero-overlay"></div>
        <div className="container">
          <nav className="breadcrumb">
            <Link to="/">Home</Link><ChevronRight size={14} /><span>Contact Us</span>
          </nav>
          <h1>Contact Us</h1>
          <p className="page-subtitle">We encourage you to contact us with any questions or comments</p>
        </div>
      </div>

      {/* Main Content */}
      <div className="contact-content">
        <div className="container">
          <div className="contact-grid">
            {/* Left Column — Info & Map */}
            <div className="contact-info">
              <h2>Get In Touch</h2>
              <p>We encourage you to contact us with any questions or comments you may have. Please call us at {practiceInfo.phone} or use the quick contact form below.</p>

              <div className="info-cards">
                <div className="info-card">
                  <div className="info-icon"><MapPin size={24} /></div>
                  <div className="info-content">
                    <h3>Our Location</h3>
                    <p>{practiceInfo.address.street}<br />{practiceInfo.address.city}, {practiceInfo.address.state} {practiceInfo.address.zip}</p>
                  </div>
                </div>
                <div className="info-card">
                  <div className="info-icon"><Phone size={24} /></div>
                  <div className="info-content">
                    <h3>Phone</h3>
                    <a href={`tel:${practiceInfo.phone.replace(/[^0-9]/g, '')}`}>{practiceInfo.phone}</a>
                  </div>
                </div>
                <div className="info-card">
                  <div className="info-icon"><Mail size={24} /></div>
                  <div className="info-content">
                    <h3>Email</h3>
                    <a href={`mailto:${practiceInfo.email}`}>{practiceInfo.email}</a>
                  </div>
                </div>
                <div className="info-card">
                  <div className="info-icon"><Clock size={24} /></div>
                  <div className="info-content">
                    <h3>Office Hours</h3>
                    <p>Mon-Tue-Thu: 9am - 6pm<br />Wed: 9am - 5pm<br />Fri: 9am - 3pm<br />Sat: 9am - 2pm</p>
                  </div>
                </div>
              </div>

              {/* Google Map Embed */}
              <div className="contact-map">
                <h3><Navigation size={18} /> Find Us</h3>
                <div className="map-wrapper">
                  <iframe
                    title="P3 Dental Location"
                    src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3046.2!2d-75.0155!3d40.1022!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x89c6b4e8f6b4b8f7%3A0x1234567890abcdef!2s10000+E+Roosevelt+Blvd%2C+Philadelphia%2C+PA+19116!5e0!3m2!1sen!2sus!4v1700000000000!5m2!1sen!2sus"
                    width="100%"
                    height="250"
                    style={{ border: 0, borderRadius: 'var(--radius-md)' }}
                    allowFullScreen=""
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                  ></iframe>
                </div>
                <a
                  href="https://www.google.com/maps/dir/?api=1&destination=10000+E+Roosevelt+Blvd+Philadelphia+PA+19116"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="directions-link"
                >
                  <MapPin size={16} /> Get Directions
                </a>
              </div>
            </div>

            {/* Right Column — Form */}
            <div className="contact-form-wrapper" ref={formRef}>
              <h2>Send Us a Message</h2>

              {submitResult?.type === 'success' ? (
                <div className="success-message">
                  <div className="success-icon-wrapper">
                    <CheckCircle size={52} />
                  </div>
                  <h3>Message Sent!</h3>
                  <p>{submitResult.message}</p>

                  {submitResult.data?.referenceNumber && (
                    <div className="confirmation-box">
                      <span className="confirmation-label">Your Reference Number</span>
                      <div className="confirmation-number-row">
                        <span className="confirmation-code">{submitResult.data.referenceNumber}</span>
                        <button
                          type="button"
                          className="copy-btn"
                          onClick={() => copyReference(submitResult.data.referenceNumber)}
                          title="Copy to clipboard"
                        >
                          {copied ? <ClipboardCheck size={18} /> : <Copy size={18} />}
                          {copied ? 'Copied!' : 'Copy'}
                        </button>
                      </div>
                      <span className="confirmation-hint">Save this for your records</span>
                    </div>
                  )}

                  <button className="btn btn-primary" onClick={resetForm}>
                    Send Another Message
                  </button>
                </div>
              ) : (
                <>
                  {submitResult?.type === 'error' && (
                    <div className="error-alert">
                      <AlertCircle size={20} />
                      <span>{submitResult.message}</span>
                    </div>
                  )}

                  <form className="contact-form" onSubmit={handleSubmit} noValidate>
                    <div className={`form-group ${fieldErrors.name ? 'has-error' : ''}`}>
                      <label htmlFor="ct-name">
                        <User size={16} /> Full Name <span className="required">*</span>
                      </label>
                      <input
                        type="text" id="ct-name" name="name"
                        value={formData.name} onChange={handleChange}
                        placeholder="Enter your full name"
                      />
                      {renderFieldError('name')}
                    </div>

                    <div className="form-row">
                      <div className={`form-group ${fieldErrors.email ? 'has-error' : ''}`}>
                        <label htmlFor="ct-email">
                          <Mail size={16} /> Email <span className="required">*</span>
                        </label>
                        <input
                          type="email" id="ct-email" name="email"
                          value={formData.email} onChange={handleChange}
                          placeholder="your@email.com"
                        />
                        {renderFieldError('email')}
                      </div>
                      <div className={`form-group ${fieldErrors.phone ? 'has-error' : ''}`}>
                        <label htmlFor="ct-phone">
                          <Phone size={16} /> Phone
                        </label>
                        <input
                          type="tel" id="ct-phone" name="phone"
                          value={formData.phone} onChange={handleChange}
                          placeholder="(215) 555-0000"
                        />
                        {renderFieldError('phone')}
                      </div>
                    </div>

                    <div className="form-group">
                      <label htmlFor="ct-subject">
                        <FileText size={16} /> Subject
                      </label>
                      <select
                        id="ct-subject" name="subject"
                        value={formData.subject} onChange={handleChange}
                      >
                        <option value="General Inquiry">General Inquiry</option>
                        <option value="Appointment Question">Appointment Question</option>
                        <option value="Insurance & Billing">Insurance &amp; Billing</option>
                        <option value="New Patient Information">New Patient Information</option>
                        <option value="Treatment Question">Treatment Question</option>
                        <option value="Feedback">Feedback</option>
                        <option value="Complaint">Complaint</option>
                        <option value="Website Issue">Website Issue</option>
                        <option value="Other">Other</option>
                      </select>
                    </div>

                    <div className={`form-group ${fieldErrors.message ? 'has-error' : ''}`}>
                      <label htmlFor="ct-message">
                        <MessageSquare size={16} /> Message <span className="required">*</span>
                      </label>
                      <textarea
                        id="ct-message" name="message"
                        value={formData.message} onChange={handleChange}
                        rows="5"
                        placeholder="Type your message here. (Messages sent using this form are not considered private. Avoid sending confidential information via email.)"
                        maxLength={2000}
                      ></textarea>
                      <div className="textarea-footer">
                        {renderFieldError('message')}
                        <span className="char-count">{charCount}/2000</span>
                      </div>
                    </div>

                    <div className="form-group checkbox-group">
                      <label className="checkbox-label">
                        <input
                          type="checkbox"
                          checked={agreeToSms}
                          onChange={(e) => setAgreeToSms(e.target.checked)}
                        />
                        <span className="checkmark"></span>
                        <span className="checkbox-text">
                          By checking this box, I consent to receive SMS/text messages from P3 Dental for follow-up communication regarding this inquiry. Msg and data rates may apply. Reply STOP to cancel.
                        </span>
                      </label>
                    </div>

                    <div className="form-note">
                      <p><strong>Note:</strong> Messages sent using this form are not considered private. Please contact our office by telephone if sending highly confidential or private information.</p>
                    </div>

                    <button
                      type="submit"
                      className="btn btn-primary btn-submit"
                      disabled={isSubmitting}
                    >
                      {isSubmitting ? 'Sending...' : <><Send size={18} /> Send Message</>}
                    </button>
                  </form>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </main>
  );
}

export default ContactPage;
